import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Paper,
  Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const GameLogModal = ({ open, onClose, playerData, gameLogData, loading, upcomingGame, sport = 'NFL' }) => {
  if (!open) return null;

  // Console log all the data for debugging
  console.log('=== GAME LOG MODAL DATA ===');
  console.log('playerData:', playerData);
  console.log('gameLogData:', gameLogData);
  console.log('upcomingGame:', upcomingGame);
  console.log('sport:', sport);
  console.log('===========================');

  // Helper to get bio data - fallback to gameLogData[0] if not in playerData
  const getBioData = () => {
    const firstGame = gameLogData?.[0];
    
    // Calculate age from birthDate
    const calculateAge = (birthDate) => {
      if (!birthDate) return null;
      const today = new Date();
      const birth = new Date(birthDate);
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      return age;
    };
    
    return {
      Height: playerData?.Height || firstGame?.height || null,
      Weight: playerData?.Weight || firstGame?.weight || null,
      Age: playerData?.Age || calculateAge(firstGame?.birthDate) || null,
      College: playerData?.College || firstGame?.college || null,
      Experience: playerData?.Experience !== undefined ? playerData.Experience : firstGame?.experience
    };
  };
  
  const bioData = getBioData();
  console.log('Resolved bioData:', bioData);

  // Format date for display - NBA uses MM/DD format, NFL uses fallback to week/season
  const formatDate = (dateString, week, season) => {
    if (dateString) {
      const date = new Date(dateString);
      if (sport === 'NBA') {
        // NBA format: 11/10
        return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
      }
      // NFL format: MM/DD/YY
      return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
    }
    // Fallback to Week X format if no date (NFL)
    if (week && season) {
      return `Wk ${week}`;
    }
    return '-';
  };

  // Format game result - returns JSX with only W/L colored
  const formatResult = (gameResult, awayScore, homeScore) => {
    if (awayScore === null || homeScore === null || !gameResult) return '-';
    
    const color = getResultColor(gameResult);
    const score = `${awayScore}-${homeScore}`;
    
    return (
      <>
        <span style={{ color: color, fontWeight: 600 }}>{gameResult}</span>
        {' '}
        {score}
      </>
    );
  };

  // Get color for game result
  const getResultColor = (gameResult) => {
    if (gameResult === 'W') return '#4caf50'; // green
    if (gameResult === 'L') return '#f44336'; // red
    if (gameResult === 'T') return '#ff9800'; // orange
    return '#fff';
  };

  // Calculate Half PPR (PPR - 0.5 * receptions)
  const calculateHalfPPR = (ppr, receptions) => {
    if (ppr === null || ppr === undefined || receptions === null || receptions === undefined) return null;
    return ppr - (0.5 * receptions);
  };

  // Format matchup using homeAway field - fallback to teamAbbr if needed
  const formatMatchup = (opponent, homeAway, awayTeam, homeTeam, teamAbbr) => {
    // If we have opponent from the calculated field
    if (opponent) {
      return homeAway === 'Home' ? `vs ${opponent}` : `@ ${opponent}`;
    }
    // Fallback: use awayTeam/homeTeam and figure out opponent
    if (awayTeam && homeTeam && teamAbbr) {
      if (teamAbbr === homeTeam) {
        return `vs ${awayTeam}`;
      } else if (teamAbbr === awayTeam) {
        return `@ ${homeTeam}`;
      }
    }
    return '-';
  };

  // Format upcoming game time
  const formatGameTime = (dateTime) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    let dayText = '';
    if (date.toDateString() === today.toDateString()) {
      dayText = 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      dayText = 'Tomorrow';
    } else {
      dayText = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
    
    const timeText = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${dayText}, ${timeText}`;
  };

  // Determine if player is a QB
  const isQB = playerData?.Position === 'QB';

  // Format percentage - NBA API returns whole numbers (41.7), not decimals (0.417)
  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '0';
    return value.toFixed(1);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1e1e1e',
          backgroundImage: 'none',
          borderRadius: 2,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box sx={{ 
          p: 3, 
          pb: 2,
          display: 'flex', 
          alignItems: 'flex-start', 
          justifyContent: 'space-between',
          borderBottom: '1px solid #333',
          gap: 3
        }}>
          {/* Left side - Player info */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>

            <Box>
              {/* Player name and team */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                  {playerData?.PlayerName || 'Player'}
                </Typography>
                {playerData?.JerseyNumber && (
                  <Chip 
                    label={`#${playerData.JerseyNumber}`}
                    size="small"
                    sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  />
                )}
              </Box>
              
              <Typography variant="body1" sx={{ color: '#aaa', mb: 1 }}>
                {playerData?.Team || playerData?.TeamName || ''} | {playerData?.Position || ''}
              </Typography>
              
              {/* Bio info row - inline format */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {bioData.Age && (
                  <Typography variant="body2" sx={{ color: '#bbb', fontSize: '0.9rem' }}>
                    <Typography component="span" sx={{ color: '#888', fontSize: '0.75rem', mr: 0.5 }}>AGE</Typography>
                    {bioData.Age}
                  </Typography>
                )}
                {bioData.Height && (
                  <Typography variant="body2" sx={{ color: '#bbb', fontSize: '0.9rem' }}>
                    <Typography component="span" sx={{ color: '#888', fontSize: '0.75rem', mr: 0.5 }}>HEIGHT</Typography>
                    {bioData.Height}
                  </Typography>
                )}
                {bioData.Weight && (
                  <Typography variant="body2" sx={{ color: '#bbb', fontSize: '0.9rem' }}>
                    <Typography component="span" sx={{ color: '#888', fontSize: '0.75rem', mr: 0.5 }}>WEIGHT</Typography>
                    {bioData.Weight} lbs
                  </Typography>
                )}
                {bioData.Experience !== undefined && bioData.Experience !== null && (
                  <Typography variant="body2" sx={{ color: '#bbb', fontSize: '0.9rem' }}>
                    <Typography component="span" sx={{ color: '#888', fontSize: '0.75rem', mr: 0.5 }}>EXP</Typography>
                    {bioData.Experience === 0 ? 'R' : bioData.Experience}
                  </Typography>
                )}
                {bioData.College && (
                  <Typography variant="body2" sx={{ color: '#bbb', fontSize: '0.9rem' }}>
                    <Typography component="span" sx={{ color: '#888', fontSize: '0.75rem', mr: 0.5 }}>COLLEGE</Typography>
                    {bioData.College}
                  </Typography>
                )}
              </Box>
              
              {/* Upcoming Game - inline with logos */}
              {(upcomingGame || playerData?.EventStart) && (
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mt: 1.5, pt: 1.5, borderTop: '1px solid #333' }}>
                  {(() => {
                    // Get team info
                    let awayTeam, homeTeam, awayLogo, homeLogo, gameTime;
                    
                    if (playerData?.EventStart) {
                      awayTeam = playerData.AwayShort;
                      homeTeam = playerData.HomeShort;
                      awayLogo = playerData.AwayLogo;
                      homeLogo = playerData.HomeLogo;
                      gameTime = playerData.EventStart;
                    } else if (upcomingGame) {
                      awayTeam = upcomingGame.awayTeam;
                      homeTeam = upcomingGame.homeTeam;
                      gameTime = upcomingGame.dateTime || upcomingGame.EventStart;
                    }
                    
                    if (!awayTeam || !homeTeam) return null;
                    
                    const formatGameDate = (dateTime) => {
                      if (!dateTime) return '';
                      const date = new Date(dateTime);
                      const today = new Date();
                      const tomorrow = new Date(today);
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      
                      let dayText;
                      if (date.toDateString() === today.toDateString()) {
                        dayText = 'Today';
                      } else if (date.toDateString() === tomorrow.toDateString()) {
                        dayText = 'Tomorrow';
                      } else {
                        dayText = date.toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        });
                      }
                      
                      const timeText = date.toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit' 
                      });
                      
                      return `${dayText} ${timeText}`;
                    };
                    
                    return (
                      <>
                        {/* Away Team Logo */}
                        {awayLogo && (
                          <img 
                            src={awayLogo} 
                            alt={awayTeam}
                            style={{ width: 24, height: 24, objectFit: 'contain' }}
                          />
                        )}
                        
                        {/* Matchup - Full format */}
                        <Typography variant="body2" sx={{ color: '#bbb', fontSize: '0.9rem', fontWeight: 500 }}>
                          {awayTeam} @ {homeTeam}
                        </Typography>
                        
                        {/* Home Team Logo */}
                        {homeLogo && (
                          <img 
                            src={homeLogo} 
                            alt={homeTeam}
                            style={{ width: 24, height: 24, objectFit: 'contain' }}
                          />
                        )}
                        
                        {/* Separator & Game Time */}
                        {gameTime && (
                          <>
                            <Typography component="span" sx={{ color: '#555', fontSize: '0.75rem', mx: -0.5 }}>•</Typography>
                            <Typography variant="body2" sx={{ color: '#bbb', fontSize: '0.9rem' }}>
                              {formatGameDate(gameTime)}
                            </Typography>
                          </>
                        )}
                      </>
                    );
                  })()}
                </Box>
              )}
            </Box>
          </Box>
          
          {/* Right side - Close button */}
          <IconButton 
            onClick={onClose}
            sx={{ 
              color: '#fff',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Game Log Table */}
        <Box sx={{ p: 3, overflowX: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#ff1744' }} />
            </Box>
          ) : !gameLogData || gameLogData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: '#aaa' }}>No game log data available</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ ml: -3 }}>
              <Table size="small" sx={{ '& td:first-of-type, & th:first-of-type': { pl: 3 } }}>
                <TableHead>
                  {/* Group Header Row */}
                  <TableRow>
                    <TableCell 
                      colSpan={3} 
                      sx={{ 
                        borderBottom: 'none',
                        backgroundColor: '#252525',
                        verticalAlign: 'middle',
                        height: '28px'
                      }}
                    ></TableCell>
                    
                    {sport === 'NBA' && (
                      <>
                        {/* Stats Group Headers */}
                        <TableCell 
                          colSpan={8} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          STATS
                        </TableCell>
                        <TableCell 
                          colSpan={2} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          FIELD GOALS
                        </TableCell>
                        <TableCell 
                          colSpan={2} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          3-POINTERS
                        </TableCell>
                        <TableCell 
                          colSpan={2} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          FREE THROWS
                        </TableCell>
                      </>
                    )}
                    
                    {sport === 'NFL' && isQB && (
                      <>
                        {/* Fantasy Points Group Header */}
                        <TableCell 
                          colSpan={2} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          FANTASY
                        </TableCell>
                        <TableCell 
                          colSpan={5} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          PASS
                        </TableCell>
                        <TableCell 
                          colSpan={4} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          RUSHING
                        </TableCell>
                        <TableCell 
                          colSpan={2} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          SACKED
                        </TableCell>
                        <TableCell 
                          colSpan={2} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          FUMBLE
                        </TableCell>
                      </>
                    )}
                    
                    {sport === 'NFL' && playerData?.Position === 'RB' && (
                      <>
                        {/* Fantasy Points Group Header */}
                        <TableCell 
                          colSpan={2} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          FANTASY
                        </TableCell>
                        <TableCell 
                          colSpan={4} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          RUSHING
                        </TableCell>
                        <TableCell 
                          colSpan={6} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          RECEIVING
                        </TableCell>
                        <TableCell 
                          colSpan={2} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          FUMBLE
                        </TableCell>
                      </>
                    )}
                    
                    {sport === 'NFL' && (playerData?.Position === 'WR' || playerData?.Position === 'TE') && (
                      <>
                        {/* Fantasy Points Group Header */}
                        <TableCell 
                          colSpan={2} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          FANTASY
                        </TableCell>
                        <TableCell 
                          colSpan={6} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          RECEIVING
                        </TableCell>
                        <TableCell 
                          colSpan={2} 
                          sx={{ 
                            color: '#aaa', 
                            fontWeight: 700, 
                            borderBottom: 'none', 
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            backgroundColor: '#252525',
                            verticalAlign: 'middle',
                            height: '28px'
                          }}
                        >
                          FUMBLE
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                  
                  {/* Column Header Row */}
                  <TableRow>
                    <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem' }}>DATE</TableCell>
                    <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem' }}>OPP</TableCell>
                    <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem' }}>RESULT</TableCell>
                    
                    {sport === 'NBA' && (
                      <>
                        {/* Stats */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>MIN</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>PTS</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>REB</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>AST</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>BLK</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>STL</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>PF</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>TO</TableCell>
                        {/* Field Goals */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>FGM-A</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>FG%</TableCell>
                        {/* 3-Pointers */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>3PM-A</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>3P%</TableCell>
                        {/* Free Throws */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>FTM-A</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>FT%</TableCell>
                      </>
                    )}
                    
                    {sport === 'NFL' && isQB && (
                      <>
                        {/* Fantasy Points */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>½ PPR</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>PPR</TableCell>
                        {/* Passing */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>ATT</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>CMP</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>YD</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>TD</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>INT</TableCell>
                        {/* Rushing */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>ATT</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>YD</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>YPC</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>TD</TableCell>
                        {/* Sacked */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>SK</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>YDS</TableCell>
                        {/* Fumble */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>FM</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>LOST</TableCell>
                      </>
                    )}
                    
                    {sport === 'NFL' && playerData?.Position === 'RB' && (
                      <>
                        {/* Fantasy Points */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>½ PPR</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>PPR</TableCell>
                        {/* Rushing */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>ATT</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>YD</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>YPC</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>TD</TableCell>
                        {/* Receiving */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>TAR</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>REC</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>YD</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>YPT</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>YPC</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>TD</TableCell>
                        {/* Fumble */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>FM</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>LOST</TableCell>
                      </>
                    )}
                    
                    {sport === 'NFL' && (playerData?.Position === 'WR' || playerData?.Position === 'TE') && (
                      <>
                        {/* Fantasy Points */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>½ PPR</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>PPR</TableCell>
                        {/* Receiving */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>TAR</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>REC</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>YD</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>YPT</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>YPC</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>TD</TableCell>
                        {/* Fumble */}
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>FM</TableCell>
                        <TableCell sx={{ color: '#aaa', fontWeight: 600, borderTop: 'none', borderBottom: '1px solid #333', fontSize: '0.75rem', textAlign: 'center' }}>LOST</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...gameLogData]
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((game, index) => {
                      // Calculate stats
                      const yardsPerTarget = game.receivingTargets > 0 
                        ? (game.receivingYards / game.receivingTargets).toFixed(1)
                        : '-';
                      const yardsPerCatch = game.receptions > 0
                        ? (game.receivingYards / game.receptions).toFixed(1)
                        : '-';
                      const rushYardsPerCarry = game.rushingAttempts > 0
                        ? (game.rushingYards / game.rushingAttempts).toFixed(1)
                        : '-';
                      
                      return (
                        <TableRow 
                          key={index}
                          sx={{ 
                            backgroundColor: index % 2 === 1 ? '#2a2a2a' : 'transparent',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                            '& td': { borderBottom: '1px solid #2a2a2a' }
                          }}
                        >
                          <TableCell sx={{ color: '#fff', fontSize: '0.813rem' }}>
                            {formatDate(game.date, game.week, game.season)}
                          </TableCell>
                          <TableCell sx={{ color: '#fff', fontSize: '0.813rem' }}>
                            {formatMatchup(game.opponent, game.homeAway, game.awayTeam, game.homeTeam, game.teamAbbr)}
                          </TableCell>
                          <TableCell sx={{ color: '#fff', fontSize: '0.813rem' }}>
                            {formatResult(game.gameResult, game.awayScore, game.homeScore)}
                          </TableCell>
                          
                          {sport === 'NBA' && (
                            <>
                              {/* Minutes */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.Minutes !== null && game.Minutes !== undefined ? game.Minutes : '-'}
                              </TableCell>
                              {/* Points */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.Points !== null && game.Points !== undefined ? game.Points : '-'}
                              </TableCell>
                              {/* Rebounds */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.Rebounds !== null && game.Rebounds !== undefined ? game.Rebounds : '-'}
                              </TableCell>
                              {/* Assists */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.Assists !== null && game.Assists !== undefined ? game.Assists : '-'}
                              </TableCell>
                              {/* Blocks */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.BlockedShots !== null && game.BlockedShots !== undefined ? game.BlockedShots : '-'}
                              </TableCell>
                              {/* Steals */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.Steals !== null && game.Steals !== undefined ? game.Steals : '-'}
                              </TableCell>
                              {/* Personal Fouls */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.PersonalFouls !== null && game.PersonalFouls !== undefined ? game.PersonalFouls : '-'}
                              </TableCell>
                              {/* Turnovers */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.Turnovers !== null && game.Turnovers !== undefined ? game.Turnovers : '-'}
                              </TableCell>
                              {/* Field Goals Made-Attempted */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {(game.FieldGoalsMade !== null && game.FieldGoalsAttempted !== null) 
                                  ? `${game.FieldGoalsMade}-${game.FieldGoalsAttempted}` 
                                  : '-'}
                              </TableCell>
                              {/* Field Goal Percentage */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.FieldGoalsPercentage !== null && game.FieldGoalsPercentage !== undefined 
                                  ? formatPercentage(game.FieldGoalsPercentage) 
                                  : '0'}
                              </TableCell>
                              {/* Three Pointers Made-Attempted */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {(game.ThreePointersMade !== null && game.ThreePointersAttempted !== null) 
                                  ? `${game.ThreePointersMade}-${game.ThreePointersAttempted}` 
                                  : '-'}
                              </TableCell>
                              {/* Three Point Percentage */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.ThreePointersPercentage !== null && game.ThreePointersPercentage !== undefined 
                                  ? formatPercentage(game.ThreePointersPercentage) 
                                  : '0'}
                              </TableCell>
                              {/* Free Throws Made-Attempted */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {(game.FreeThrowsMade !== null && game.FreeThrowsAttempted !== null) 
                                  ? `${game.FreeThrowsMade}-${game.FreeThrowsAttempted}` 
                                  : '-'}
                              </TableCell>
                              {/* Free Throw Percentage */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.FreeThrowsPercentage !== null && game.FreeThrowsPercentage !== undefined 
                                  ? formatPercentage(game.FreeThrowsPercentage) 
                                  : '0'}
                              </TableCell>
                            </>
                          )}
                          
                          {sport === 'NFL' && isQB && (
                            <>
                              {/* Fantasy Points */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center', fontWeight: 600 }}>
                                {calculateHalfPPR(game.FantasyPointsPPR, game.receptions)?.toFixed(1) || '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center', fontWeight: 600 }}>
                                {game.FantasyPointsPPR?.toFixed(1) || '-'}
                              </TableCell>
                              
                              {/* Passing */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.passingAttempts !== null && game.passingAttempts !== undefined ? game.passingAttempts : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.passingCompletions !== null && game.passingCompletions !== undefined ? game.passingCompletions : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center', fontWeight: 600 }}>
                                {game.passingYards !== null && game.passingYards !== undefined ? game.passingYards : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.passingTouchdowns !== null && game.passingTouchdowns !== undefined ? game.passingTouchdowns : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.passingInterceptions !== null && game.passingInterceptions !== undefined ? game.passingInterceptions : '-'}
                              </TableCell>
                              {/* Rushing */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.rushingAttempts !== null && game.rushingAttempts !== undefined ? game.rushingAttempts : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.rushingYards !== null && game.rushingYards !== undefined ? game.rushingYards : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.rushingAttempts > 0 ? rushYardsPerCarry : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.rushingTouchdowns !== null && game.rushingTouchdowns !== undefined ? game.rushingTouchdowns : '-'}
                              </TableCell>
                              {/* Sacked */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.passingSacks !== null && game.passingSacks !== undefined ? game.passingSacks : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.passingSackYards !== null && game.passingSackYards !== undefined ? game.passingSackYards : '-'}
                              </TableCell>
                              {/* Fumble */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.fumbles !== null && game.fumbles !== undefined ? game.fumbles : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.fumblesLost !== null && game.fumblesLost !== undefined ? game.fumblesLost : '-'}
                              </TableCell>
                            </>
                          )}
                          
                          {sport === 'NFL' && playerData?.Position === 'RB' && (
                            <>
                              {/* Fantasy Points */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center', fontWeight: 600 }}>
                                {calculateHalfPPR(game.FantasyPointsPPR, game.receptions)?.toFixed(1) || '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center', fontWeight: 600 }}>
                                {game.FantasyPointsPPR?.toFixed(1) || '-'}
                              </TableCell>
                              
                              {/* Rushing */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.rushingAttempts !== null && game.rushingAttempts !== undefined ? game.rushingAttempts : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center', fontWeight: 600 }}>
                                {game.rushingYards !== null && game.rushingYards !== undefined ? game.rushingYards : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.rushingAttempts > 0 ? rushYardsPerCarry : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.rushingTouchdowns !== null && game.rushingTouchdowns !== undefined ? game.rushingTouchdowns : '-'}
                              </TableCell>
                              {/* Receiving */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.receivingTargets !== null && game.receivingTargets !== undefined ? game.receivingTargets : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.receptions !== null && game.receptions !== undefined ? game.receptions : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center', fontWeight: 600 }}>
                                {game.receivingYards !== null && game.receivingYards !== undefined ? game.receivingYards : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.receivingTargets > 0 ? yardsPerTarget : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.receptions > 0 ? yardsPerCatch : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.receivingTouchdowns !== null && game.receivingTouchdowns !== undefined ? game.receivingTouchdowns : '-'}
                              </TableCell>
                              {/* Fumble */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.fumbles !== null && game.fumbles !== undefined ? game.fumbles : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.fumblesLost !== null && game.fumblesLost !== undefined ? game.fumblesLost : '-'}
                              </TableCell>
                            </>
                          )}
                          
                          {sport === 'NFL' && (playerData?.Position === 'WR' || playerData?.Position === 'TE') && (
                            <>
                              {/* Fantasy Points */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center', fontWeight: 600 }}>
                                {calculateHalfPPR(game.FantasyPointsPPR, game.receptions)?.toFixed(1) || '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center', fontWeight: 600 }}>
                                {game.FantasyPointsPPR?.toFixed(1) || '-'}
                              </TableCell>
                              
                              {/* Receiving */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.receivingTargets !== null && game.receivingTargets !== undefined ? game.receivingTargets : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.receptions !== null && game.receptions !== undefined ? game.receptions : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center', fontWeight: 600 }}>
                                {game.receivingYards !== null && game.receivingYards !== undefined ? game.receivingYards : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.receivingTargets > 0 ? yardsPerTarget : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.receptions > 0 ? yardsPerCatch : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.receivingTouchdowns !== null && game.receivingTouchdowns !== undefined ? game.receivingTouchdowns : '-'}
                              </TableCell>
                              {/* Fumble */}
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.fumbles !== null && game.fumbles !== undefined ? game.fumbles : '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontSize: '0.813rem', textAlign: 'center' }}>
                                {game.fumblesLost !== null && game.fumblesLost !== undefined ? game.fumblesLost : '-'}
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default GameLogModal;